import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type Surface = "negotiate" | "verify";

/** Dashboard modes control which sidebar sections are visible */
export type DashboardMode = "full" | "audit" | "deals" | "agents" | "custom";

export const DASHBOARD_MODE_PRESETS: Record<Exclude<DashboardMode, "custom">, { label: string; description: string; visibleGroups: string[] }> = {
  full: {
    label: "Everything",
    description: "All features: loads, partners, verification, compliance, analytics",
    visibleGroups: ["Overview", "Loads & Dispatch", "Fleet & Network", "Documents & Finance", "Verify & Protect", "Analytics & Alerts", "Compliance", "Admin", "Settings"],
  },
  audit: {
    label: "Phase 1 · Verification & Compliance",
    description: "Verification, compliance, intelligence, and settings. Used by pilot dispatchers during rollout.",
    visibleGroups: ["Overview", "Compliance & Intelligence", "Email", "Settings"],
  },
  deals: {
    label: "Loads & Rates",
    description: "Load management, rate negotiation, invoices, marketplace",
    visibleGroups: ["Overview", "Loads & Dispatch", "Fleet & Network", "Documents & Finance", "Settings"],
  },
  agents: {
    label: "Advanced",
    description: "Partner identity, directory, chat, API keys",
    visibleGroups: ["Overview", "Loads & Dispatch", "Fleet & Network", "Settings"],
  },
};

interface CompanyModeContextType {
  mode: "test" | "live";
  setMode: (mode: "test" | "live") => Promise<void>;
  isTest: boolean;
  surface: Surface;
  setSurface: (surface: Surface) => void;
  dashboardMode: DashboardMode;
  setDashboardMode: (mode: DashboardMode) => Promise<void>;
  /**
   * False until the authoritative companies.settings.dashboard_mode has been
   * read from the DB. dashboardMode is seeded synchronously from localStorage,
   * which can be a stale Phase 2 value — gates must wait for this to flip true
   * before trusting the phase, to avoid flashing Phase 2 content to a tenant
   * the DB will resolve as Phase 1.
   */
  dashboardModeLoaded: boolean;
  visibleGroups: string[];
  customGroups: string[];
  setCustomGroups: (groups: string[]) => Promise<void>;
}

const SURFACE_KEY = "cr-surface";
const DASHBOARD_MODE_KEY = "cr-dashboard-mode";

const CompanyModeContext = createContext<CompanyModeContextType>({
  mode: "test",
  setMode: async () => {},
  isTest: true,
  surface: "negotiate",
  setSurface: () => {},
  dashboardMode: "audit",
  setDashboardMode: async () => {},
  dashboardModeLoaded: false,
  visibleGroups: DASHBOARD_MODE_PRESETS.audit.visibleGroups,
  customGroups: [],
  setCustomGroups: async () => {},
});

export function CompanyModeProvider({ children }: { children: ReactNode }) {
  const { companyId } = useAuth();
  const [mode, setModeState] = useState<"test" | "live">("test");
  const [surface, setSurfaceState] = useState<Surface>(() => {
    try {
      const stored = localStorage.getItem(SURFACE_KEY);
      return stored === "verify" ? "verify" : "negotiate";
    } catch {
      return "negotiate";
    }
  });
  const [dashboardMode, setDashboardModeState] = useState<DashboardMode>(() => {
    // Phase 1 launch migration: force audit mode for everyone except
    // explicit platform admins. Users with stale "full" mode from
    // earlier sessions get moved to the clean Phase 1 experience.
    // To override, set localStorage.setItem("cr-dashboard-mode", "full")
    // in DevTools (for Jack/Louie admin testing).
    try {
      const stored = localStorage.getItem(DASHBOARD_MODE_KEY);
      // If the stored value is "full" or missing, migrate to "audit".
      // Only honor non-full explicit choices (audit, deals, agents, custom).
      if (!stored || stored === "full") {
        localStorage.setItem(DASHBOARD_MODE_KEY, "audit");
        return "audit";
      }
      return stored as DashboardMode;
    } catch {
      return "audit";
    }
  });
  const [customGroups, setCustomGroupsState] = useState<string[]>([]);
  const [dashboardModeLoaded, setDashboardModeLoaded] = useState(false);

  useEffect(() => {
    // No company in scope: there is no DB phase to wait for, so the conservative
    // localStorage/default ("audit" = Phase 1, most restrictive) is authoritative.
    // Mark loaded so gates don't hang on a permanent loading state.
    if (!companyId) {
      setDashboardModeLoaded(true);
      return;
    }
    setDashboardModeLoaded(false);
    const cached = sessionStorage.getItem(`cr-company-mode-${companyId}`);
    if (cached) { setModeState(cached as "test" | "live"); }
    const loadData = async () => {
      try {
        const { data } = await supabase
          .from("companies")
          .select("mode, settings")
          .eq("id", companyId)
          .single();

        if (data?.mode === "live" || data?.mode === "test") {
          setModeState(data.mode);
          sessionStorage.setItem(`cr-company-mode-${companyId}`, data.mode);
        }
        // Load dashboard mode from company settings
        const settings = data?.settings as Record<string, unknown> | null;
        if (settings?.dashboard_mode) {
          const dm = settings.dashboard_mode as DashboardMode;
          setDashboardModeState(dm);
          try { localStorage.setItem(DASHBOARD_MODE_KEY, dm); } catch {}
        }
        if (settings?.custom_dashboard_groups && Array.isArray(settings.custom_dashboard_groups)) {
          setCustomGroupsState(settings.custom_dashboard_groups as string[]);
        }
      } catch (err) {
        /* query/network error — fall back to current/default mode */
      } finally {
        setDashboardModeLoaded(true);
      }
    };
    loadData();
  }, [companyId]);

  const setMode = useCallback(
    async (newMode: "test" | "live") => {
      if (!companyId) return;
      await supabase.from("companies").update({ mode: newMode }).eq("id", companyId);
      setModeState(newMode);
      sessionStorage.setItem(`cr-company-mode-${companyId}`, newMode);
    },
    [companyId]
  );

  const setSurface = useCallback((s: Surface) => {
    setSurfaceState(s);
    try { localStorage.setItem(SURFACE_KEY, s); } catch {}
  }, []);

  const setDashboardMode = useCallback(async (dm: DashboardMode) => {
    setDashboardModeState(dm);
    try { localStorage.setItem(DASHBOARD_MODE_KEY, dm); } catch {}
    if (companyId) {
      const { data: existing } = await supabase.from("companies").select("settings").eq("id", companyId).single();
      const currentSettings = (existing?.settings as Record<string, unknown>) ?? {};
      await supabase.from("companies").update({
        settings: { ...currentSettings, dashboard_mode: dm },
      }).eq("id", companyId);
    }
  }, [companyId]);

  const setCustomGroups = useCallback(async (groups: string[]) => {
    setCustomGroupsState(groups);
    if (companyId) {
      const { data: existing } = await supabase.from("companies").select("settings").eq("id", companyId).single();
      const currentSettings = (existing?.settings as Record<string, unknown>) ?? {};
      await supabase.from("companies").update({
        settings: { ...currentSettings, dashboard_mode: "custom", custom_dashboard_groups: groups },
      }).eq("id", companyId);
    }
    setDashboardModeState("custom");
    try { localStorage.setItem(DASHBOARD_MODE_KEY, "custom"); } catch {}
  }, [companyId]);

  const visibleGroups = dashboardMode === "custom"
    ? customGroups
    : DASHBOARD_MODE_PRESETS[dashboardMode]?.visibleGroups ?? DASHBOARD_MODE_PRESETS.full.visibleGroups;

  return (
    <CompanyModeContext.Provider value={{
      mode, setMode, isTest: mode === "test", surface, setSurface,
      dashboardMode, setDashboardMode, dashboardModeLoaded, visibleGroups, customGroups, setCustomGroups,
    }}>
      {children}
    </CompanyModeContext.Provider>
  );
}

export const useCompanyMode = () => useContext(CompanyModeContext);
